import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RiHome2Line } from "react-icons/ri";
import BasicTextFields from "../../components/textfield/textField";
import Pageheading from "../../components/PageHeading/Pageheading";
import Button from "../../components/Buttons/Button";
import InputFileUpload from "../../components/FileUpload/InputFileUpload";
import { useWindowSize } from "react-use";
import contactimg from "../../assets/careers/Banner.png";
import mobileimg from "../../assets/careers/Banner_Mobile.png";
import BannerHeading from "../../components/BannerHeading/Heading";
import Joi from "joi";
import axios from "axios";
import config from "../../config/config";

const allowedTypes = config.fileValidation.documentTypes;
const maxSize = config.fileValidation.defaultMaxSizeMB * 1024 * 1024;

const careerSchema = Joi.object({
  name: Joi.string().required().label("First Name"),
  Last_name: Joi.string().required().label("Last Name"),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .label("Email"),
  contactDetails: Joi.string()
    .length(10)
    .pattern(/^[0-9]+$/)
    .required()
    .label("Contact Number"),
  experience: Joi.string().required().label("Experience"),
  location: Joi.string().trim().required().label("Location"),
  resume: Joi.any()
    .custom((value, helpers) => {
      // Check by duck-typing instead of instanceof
      if (
        !value ||
        typeof value.name !== "string" ||
        typeof value.size !== "number"
      ) {
        return helpers.error("any.required");
      }
      return value;
    })
    .messages({ "any.required": "Resume is required" }),
  coverLetter: Joi.any()
    .custom((value, helpers) => {
      if (
        !value ||
        typeof value.name !== "string" ||
        typeof value.size !== "number"
      ) {
        return helpers.error("any.required");
      }
      return value;
    })
    .messages({ "any.required": "Cover Letter is required" }),
});

const Career = () => {
  const { width } = useWindowSize();
  const formRef = useRef(null);

  const [formValues, setFormValues] = useState({
    name: "",
    Last_name: "",
    email: "",
    contactDetails: "",
    experience: "",
    location: "",
    resume: null,
    coverLetter: null,
  });

  const [errors, setErrors] = useState({});
  const [fileKey, setFileKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (values) => {
    const { error } = careerSchema.validate(values, config.joiOptions);

    const newErrors = {};

    if (error) {
      error.details.forEach((err) => {
        const label = err.context?.label || err.path[0];
        if (
          err.type === "string.empty" ||
          err.type === "any.required" ||
          err.type === "any.invalid"
        ) {
          newErrors[err.path[0]] = `${label} is required`;
        } else {
          newErrors[err.path[0]] = `Enter a valid ${label}`;
        }
      });
    }

    const validateFile = (file, fieldName, label) => {
      if (!file) return;

      // check by extension as fallback since .doc type can be empty in some browsers
      const fileName = file.name?.toLowerCase() || "";
      const validExtensions = config.fileValidation.documentExtensions; // [".pdf", ".doc", ".docx"]
      const hasValidExtension = validExtensions.some((ext) =>
        fileName.endsWith(ext),
      );

      const hasValidType = allowedTypes.includes(file.type) || file.type === ""; // .doc sometimes has empty type

      if (!hasValidExtension || (!hasValidType && file.type !== "")) {
        newErrors[fieldName] = `${label} must be a PDF, DOC, or DOCX file`;
        return;
      }

      if (file.size > maxSize) {
        newErrors[fieldName] =
          `${label} must be less than ${config.fileValidation.defaultMaxSizeMB}MB`;
      }
    };

    // Extract file — handle both FileList and plain File object
    const getFile = (value) => {
      if (!value) return null;
      if (value instanceof File) return value; // ✅ direct File
      if (value instanceof FileList || Array.isArray(value)) return value[0];
      return null;
    };

    const resumeFile = getFile(values.resume);
    const coverFile = getFile(values.coverLetter);

    if (!resumeFile) {
      newErrors.resume = "Resume is required";
    } else {
      validateFile(resumeFile, "resume", "Resume");
    }
    if (!coverFile) {
      newErrors.coverLetter = "Cover Letter is required";
    } else {
      validateFile(coverFile, "coverLetter", "Cover Letter");
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (name, file) => {
    const newErrors = { ...errors };

    // ❗ USER CLICKED CANCEL → DO NOTHING
    if (!file) {
      return; // ✅ keep old file, don't show error
    }

    const fileName = file.name?.toLowerCase() || "";
    const validExtensions = config.fileValidation.documentExtensions;

    const hasValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext),
    );

    const hasValidType = allowedTypes.includes(file.type) || file.type === "";

    if (!hasValidExtension || (!hasValidType && file.type !== "")) {
      newErrors[name] = "Only PDF, DOC, and DOCX files are allowed.";
      setErrors(newErrors);

      // ❌ reject new file but KEEP old one
      return;
    }

    if (file.size > maxSize) {
      newErrors[name] =
        `File must be less than ${config.fileValidation.defaultMaxSizeMB}MB`;
      setErrors(newErrors);

      // ❌ reject new file but KEEP old one
      return;
    }

    // ✅ valid file → replace
    setFormValues((prev) => ({ ...prev, [name]: file }));
    newErrors[name] = "";
    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("formValues at submit:", formValues); // 👈 add this
    console.log("resume is File?", formValues.resume instanceof File);

    if (isSubmitting) return;

    const validationErrors = validateForm(formValues);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const formData = new FormData();
        formData.append("firstName", formValues.name);
        formData.append("lastName", formValues.Last_name);
        formData.append("email", formValues.email);
        formData.append("phone", formValues.contactDetails);
        formData.append("experience", formValues.experience);
        formData.append("location", formValues.location);
        formData.append("resume", formValues.resume);
        formData.append("coverLetter", formValues.coverLetter);

        await axios.post(`${config.apiUrl}/career-leads`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        toast.success("Form submitted successfully!");

        setFormValues({
          name: "",
          Last_name: "",
          email: "",
          contactDetails: "",
          experience: "",
          location: "",
          resume: null,
          coverLetter: null,
        });
        setFileKey((prev) => prev + 1);
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong!");
      } finally {
        setIsSubmitting(false);
      }
    }

    formRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const preloadImages = [contactimg, mobileimg];
    preloadImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <div className="w-full h-full text-white relative !overflow-hidden">
      <div className="relative w-full h-[90vh] md:h-[40vw] 3xl:h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-transparent z-10">
          <div className="md:pl-[5.63vw] pl-4 flex items-center md:pt-[10.09vw] pt-[25vw]">
            <Pageheading pageHeading={"Careers"} icon={<RiHome2Line />} />
          </div>
        </div>
        <div
          className="absolute inset-0 bg-contain md:bg-cover bg-no-repeat bg-bottom flex justify-center items-center"
          style={{
            backgroundImage: `url(${width < 768 ? mobileimg : contactimg})`,
          }}
        >
          <div className="absolute top-[25vh] md:top-[20vw] md:left-[17.47vw] flex justify-start items-center z-10">
            <BannerHeading headingText={"CAREERS"} />
          </div>
        </div>
        <div className="absolute h-[250px] md:h-[300px] tab:h-[327px] w-full top-0 bg-gradient-to-b from-[#121614]"></div>
      </div>

      {/* Introduction Section */}
      <div className="w-full px-4 pt-[60px] md:px-[7.2vw] md:pt-[100px] 3xl:pt-[120px] flex flex-col">
        <div className="w-10/12 md:w-full">
          <h2
            data-aos="fade-up"
            className="text-left w-full text-[11vw] md:text-[5.5vw] leading-[11vw] md:leading-[5.5vw] font-bebas_neue text-transparent bg-clip-text bg-text-gradient"
          >
            INTERESTED IN BEING ONE OF US?
          </h2>
        </div>
        <div
          data-aos="fade-up"
          className="font-montserrat text-left text-[!16px] sm:text-[20px] 3xl-plus:text-[30px] text-white mt-8 font-normal md:w-[67.7vw]"
        >
          At RAT Films, we are always on the lookout for passionate new talent
          who thrive on creativity and innovation. Whether you're a storyteller,
          a visual artist, or a master of logistics, we value those with a keen
          eye for detail, a hunger for excellence, and a drive to push
          boundaries. Join us and be part of a team that dares to dream big,
          celebrates collaboration, and transforms ideas into impactful
          narratives. If you're ready to bring bold ideas to life, shake up the
          norm, and create work that matters, we're ready for you. Let's make
          magic together!
        </div>
      </div>

      {/* Form Section */}
      <div
        data-aos="fade-up"
        className="relative flex flex-col w-full mt-[60px] md:mt-[100px] 3xl:mt-[120px]"
      >
        <div
          className="text-center font-montserrat"
          ref={formRef}
          style={{ scrollMarginTop: "60px" }}
        >
          <div className="bg-glass/30 backdrop-blur-3xl">
            <h2 className="md:py-[8vh] py-[3.70vh] text-[40px] leading-[40px] sm:text-[80px] sm:leading-[80px] 3xl-plus:text-[94px] 3xl-plus:leading-[94px] font-bebas_neue tracking-normal uppercase brightness-125 text-transparent bg-clip-text bg-text-gradient">
              APPLY NOW
            </h2>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col items-center px-4 md:pb-[8vh] pb-[3.70vh] md:px-[7.29vw] space-y-[3.70vh] w-full"
            >
              {/* Row 1 */}
              <div className="flex flex-col md:flex-row md:space-x-10 md:pb-[3.70vh] space-y-[3.70vh] md:space-y-0 w-full">
                <div className="flex flex-1 flex-col relative">
                  <BasicTextFields
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    text="First Name*"
                    // error={errors.name}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div className="flex flex-1 flex-col relative">
                  <BasicTextFields
                    name="Last_name"
                    value={formValues.Last_name}
                    onChange={handleChange}
                    text="Last Name*"
                    // error={errors.Last_name}
                  />
                  {errors.Last_name && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.Last_name}
                    </p>
                  )}
                </div>
                <div className="flex flex-1 flex-col relative">
                  <BasicTextFields
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                    text="Email Id*"
                    // error={errors.email}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2 */}
              <div className="flex flex-col md:flex-row md:space-x-10 md:pb-[3.70vh] space-y-[3.70vh] md:space-y-0 w-full">
                <div className="flex flex-1 flex-col relative">
                  <BasicTextFields
                    name="contactDetails"
                    value={formValues.contactDetails}
                    onChange={handleChange}
                    text="Contact No*"
                    inputModeNumeric={true}
                    // error={errors.contactDetails}
                  />
                  {errors.contactDetails && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.contactDetails}
                    </p>
                  )}
                </div>
                <div className="flex flex-1 flex-col relative">
                  <BasicTextFields
                    name="experience"
                    value={formValues.experience}
                    onChange={handleChange}
                    text="Experience*"
                    // error={errors.experience}
                  />
                  {errors.experience && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.experience}
                    </p>
                  )}
                </div>
                <div className="flex flex-1 flex-col relative">
                  <BasicTextFields
                    name="location"
                    value={formValues.location}
                    onChange={handleChange}
                    text="Where are you based?*"
                    // error={errors.location}
                  />
                  {errors.location && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.location}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 3 - File Uploads */}
              <div className="flex flex-col md:flex-row md:space-x-10 space-y-[3.70vh] md:space-y-0 w-full">
                <div className="flex flex-1 flex-col pt-3 relative">
                  <InputFileUpload
                    key={`resume-${fileKey}`}
                    text="Resume upload*"
                    onChange={(file) => handleFileChange("resume", file)}
                    value={formValues.resume}
                  />
                  {errors.resume && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.resume}
                    </p>
                  )}
                </div>
                <div className="flex flex-1 flex-col pt-2 relative">
                  <InputFileUpload
                    key={`coverLetter-${fileKey}`}
                    text="Cover Letter*"
                    onChange={(file) => handleFileChange("coverLetter", file)}
                    value={formValues.coverLetter}
                  />
                  {errors.coverLetter && (
                    <p className="text-red-500 text-[3.5vw] md:text-[1.1vw] p-1 absolute bottom-0 translate-y-[100%]">
                      {errors.coverLetter}
                    </p>
                  )}
                </div>
                <div className="flex-1 md:pt-3 md:pl-6 md:py-0 flex items-center justify-start">
                  <Button
                    buttonText={isSubmitting ? "Submitting..." : "Submit"}
                    type="submit"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </form>
          </div>
          <ToastContainer />
        </div>
      </div>
    </div>
  );
};

export default Career;
